import { makeAutoObservable, runInAction } from 'mobx'
import { apiClient } from '@/services/api'
import type {
  Workflow,
  WorkflowCreate,
  WorkflowNode,
  WorkflowEdge,
  PaginatedResponse,
  WorkflowStatus,
  N8NWorkflow,
  WorkflowTag,
  CentralizedAgent,
} from '@/types'
import { ALL_WORKFLOW_TAGS } from '@/types'

class WorkflowStore {
  workflows: Workflow[] = []
  selectedWorkflow: Workflow | null = null
  isLoading = false
  error: string | null = null

  // Pagination
  currentPage = 1
  pageSize = 10
  totalWorkflows = 0

  // Filters
  searchQuery = ''
  statusFilter: WorkflowStatus | 'all' = 'all'

  // Canvas state
  nodes: WorkflowNode[] = []
  edges: WorkflowEdge[] = []
  isDirty = false

  // N8N workflows (from n8n service)
  n8nWorkflows: N8NWorkflow[] = []
  n8nLoading = false
  n8nError: string | null = null

  // N8N workflow builders (centralized agents for building workflows)
  n8nBuilders: CentralizedAgent[] = []
  n8nBuildersLoading = false

  // Tag filtering
  selectedTags: WorkflowTag[] = []
  availableTags: WorkflowTag[] = ALL_WORKFLOW_TAGS

  constructor() {
    makeAutoObservable(this)
  }

  // Computed values
  get filteredWorkflows() {
    const workflows = this.workflows || []
    return workflows.filter((workflow) => {
      const matchesSearch =
        !this.searchQuery ||
        workflow.name.toLowerCase().includes(this.searchQuery.toLowerCase()) ||
        workflow.description?.toLowerCase().includes(this.searchQuery.toLowerCase())

      const matchesStatus =
        this.statusFilter === 'all' || workflow.status === this.statusFilter

      return matchesSearch && matchesStatus
    })
  }

  get hasActiveFilters() {
    return this.searchQuery !== '' || this.statusFilter !== 'all' || this.selectedTags.length > 0
  }

  // N8N workflows with inferred tags
  get n8nWorkflowsWithTags(): N8NWorkflow[] {
    return this.n8nWorkflows.map((workflow) => ({
      ...workflow,
      tags: this.inferN8NTags(workflow),
    }))
  }

  // Filtered N8N workflows based on search and tags
  get filteredN8NWorkflows(): N8NWorkflow[] {
    let workflows = this.n8nWorkflowsWithTags

    // Apply search filter
    if (this.searchQuery) {
      const query = this.searchQuery.toLowerCase()
      workflows = workflows.filter(
        (workflow) => workflow.name.toLowerCase().includes(query)
      )
    }

    // Apply tag filter
    if (this.selectedTags.length > 0) {
      workflows = workflows.filter((workflow) =>
        workflow.tags?.some((tag) => this.selectedTags.includes(tag))
      )
    }

    return workflows
  }

  // Infer tags from n8n workflow nodes
  inferN8NTags(workflow: N8NWorkflow): WorkflowTag[] {
    const tags: WorkflowTag[] = ['n8n', 'external']
    const nodes = workflow.nodes || []

    // Check for webhook triggers
    if (nodes.some((n) => n.type.toLowerCase().includes('webhook'))) {
      tags.push('webhook')
    }

    // Check for scheduled triggers
    if (nodes.some((n) =>
      n.type.toLowerCase().includes('cron') ||
      n.type.toLowerCase().includes('schedule') ||
      n.type.toLowerCase().includes('interval')
    )) {
      tags.push('scheduled')
    }

    // Check for AI nodes (likely ai-generated)
    if (nodes.some((n) =>
      n.type.toLowerCase().includes('openai') ||
      n.type.toLowerCase().includes('langchain') ||
      n.type.toLowerCase().includes('ai')
    )) {
      tags.push('ai-generated')
    }

    return tags
  }

  get canSave() {
    return this.isDirty && this.nodes.length > 0
  }

  // Actions
  async fetchWorkflows(workspaceId?: string) {
    this.isLoading = true
    this.error = null

    try {
      const response: PaginatedResponse<Workflow> = await apiClient.getWorkflows({
        workspace_id: workspaceId,
        skip: (this.currentPage - 1) * this.pageSize,
        limit: this.pageSize,
      })

      runInAction(() => {
        this.workflows = response.items
        this.totalWorkflows = response.total
        this.isLoading = false
      })
    } catch (error: any) {
      runInAction(() => {
        this.workflows = []
        this.error = error.response?.data?.detail || 'Failed to fetch workflows'
        this.isLoading = false
      })
    }
  }

  async fetchWorkflowById(id: string) {
    this.isLoading = true
    this.error = null

    try {
      const workflow = await apiClient.getWorkflow(id)

      runInAction(() => {
        this.selectedWorkflow = workflow
        this.nodes = workflow.definition?.nodes || []
        this.edges = workflow.definition?.edges || []
        this.isDirty = false
        this.isLoading = false
      })

      return workflow
    } catch (error: any) {
      runInAction(() => {
        this.error = error.response?.data?.detail || 'Failed to fetch workflow'
        this.isLoading = false
      })
      return null
    }
  }

  loadWorkflow(workflow: Workflow) {
    this.selectedWorkflow = workflow
    this.nodes = workflow.definition?.nodes || []
    this.edges = workflow.definition?.edges || []
    this.isDirty = false
  }

  async createWorkflow(data: WorkflowCreate) {
    this.isLoading = true
    this.error = null

    try {
      const workflow = await apiClient.createWorkflow(data)

      runInAction(() => {
        this.workflows = [workflow, ...this.workflows]
        this.totalWorkflows += 1
        this.selectedWorkflow = workflow
        this.isLoading = false
      })

      return workflow
    } catch (error: any) {
      runInAction(() => {
        this.error = error.response?.data?.detail || 'Failed to create workflow'
        this.isLoading = false
      })
      throw error
    }
  }

  async updateWorkflow(id: string, data: Partial<WorkflowCreate>) {
    this.isLoading = true
    this.error = null

    try {
      const updatedWorkflow = await apiClient.updateWorkflow(id, data)

      runInAction(() => {
        this.workflows = this.workflows.map((workflow) =>
          workflow.id === id ? updatedWorkflow : workflow
        )
        if (this.selectedWorkflow?.id === id) {
          this.selectedWorkflow = updatedWorkflow
        }
        this.isDirty = false
        this.isLoading = false
      })

      return updatedWorkflow
    } catch (error: any) {
      runInAction(() => {
        this.error = error.response?.data?.detail || 'Failed to update workflow'
        this.isLoading = false
      })
      throw error
    }
  }

  async saveWorkflow() {
    if (!this.selectedWorkflow) {
      throw new Error('No workflow selected')
    }

    const data: Partial<WorkflowCreate> = {
      definition: {
        nodes: this.nodes,
        edges: this.edges,
      },
    }

    return this.updateWorkflow(this.selectedWorkflow.id, data)
  }

  async deleteWorkflow(id: string) {
    this.isLoading = true
    this.error = null

    try {
      await apiClient.deleteWorkflow(id)

      runInAction(() => {
        this.workflows = this.workflows.filter((workflow) => workflow.id !== id)
        this.totalWorkflows -= 1
        if (this.selectedWorkflow?.id === id) {
          this.selectedWorkflow = null
          this.nodes = []
          this.edges = []
        }
        this.isLoading = false
      })
    } catch (error: any) {
      runInAction(() => {
        this.error = error.response?.data?.detail || 'Failed to delete workflow'
        this.isLoading = false
      })
      throw error
    }
  }

  async executeWorkflow(id: string, inputData: Record<string, any>) {
    this.isLoading = true
    this.error = null

    try {
      // Execute workflow endpoint (to be implemented in backend)
      const execution = await apiClient.createExecution({
        agent_id: id, // Using workflow ID as agent ID for now
        input_data: inputData,
      })

      runInAction(() => {
        this.isLoading = false
      })

      return execution
    } catch (error: any) {
      runInAction(() => {
        this.error = error.response?.data?.detail || 'Failed to execute workflow'
        this.isLoading = false
      })
      throw error
    }
  }

  // Canvas actions
  setNodes(nodes: WorkflowNode[]) {
    this.nodes = nodes
    this.isDirty = true
  }

  setEdges(edges: WorkflowEdge[]) {
    this.edges = edges
    this.isDirty = true
  }

  addNode(node: WorkflowNode) {
    this.nodes = [...this.nodes, node]
    this.isDirty = true
  }

  updateNode(id: string, data: Partial<WorkflowNode['data']>) {
    this.nodes = this.nodes.map((node) =>
      node.id === id ? { ...node, data: { ...node.data, ...data } } : node
    )
    this.isDirty = true
  }

  deleteNode(id: string) {
    this.nodes = this.nodes.filter((node) => node.id !== id)
    this.edges = this.edges.filter((edge) => edge.source !== id && edge.target !== id)
    this.isDirty = true
  }

  addEdge(edge: WorkflowEdge) {
    this.edges = [...this.edges, edge]
    this.isDirty = true
  }

  deleteEdge(id: string) {
    this.edges = this.edges.filter((edge) => edge.id !== id)
    this.isDirty = true
  }

  clearCanvas() {
    this.nodes = []
    this.edges = []
    this.isDirty = false
  }

  // N8N workflow actions
  async fetchN8NWorkflows(active?: boolean) {
    this.n8nLoading = true
    this.n8nError = null

    try {
      const workflows = await apiClient.getN8NWorkflows({ active, limit: 100 })

      runInAction(() => {
        this.n8nWorkflows = workflows
        this.n8nLoading = false
      })
    } catch (error: any) {
      runInAction(() => {
        this.n8nWorkflows = []
        this.n8nError = error.response?.data?.detail || 'Failed to fetch n8n workflows'
        this.n8nLoading = false
      })
    }
  }

  async fetchN8NBuilders() {
    this.n8nBuildersLoading = true

    try {
      const agents = await apiClient.getCentralizedAgents({ domain: 'automation' })
      const builders = agents.filter(
        (agent) =>
          agent.name.includes('n8n-workflow-builder') ||
          agent.name.includes('n8n-enhanced-workflow-builder')
      )

      runInAction(() => {
        this.n8nBuilders = builders
        this.n8nBuildersLoading = false
      })
    } catch (error: any) {
      runInAction(() => {
        this.n8nBuilders = []
        this.n8nBuildersLoading = false
      })
    }
  }

  async executeN8NWorkflow(workflowId: string, inputData?: Record<string, any>) {
    try {
      const result = await apiClient.executeN8NWorkflow(workflowId, inputData)
      return result
    } catch (error: any) {
      throw new Error(error.response?.data?.detail || 'Failed to execute n8n workflow')
    }
  }

  // Tag filter actions
  toggleTag(tag: WorkflowTag) {
    if (this.selectedTags.includes(tag)) {
      this.selectedTags = this.selectedTags.filter((t) => t !== tag)
    } else {
      this.selectedTags = [...this.selectedTags, tag]
    }
  }

  clearSelectedTags() {
    this.selectedTags = []
  }

  // Filter actions
  setSearchQuery(query: string) {
    this.searchQuery = query
  }

  setStatusFilter(status: WorkflowStatus | 'all') {
    this.statusFilter = status
  }

  clearFilters() {
    this.searchQuery = ''
    this.statusFilter = 'all'
    this.selectedTags = []
  }

  // Pagination actions
  setPage(page: number) {
    this.currentPage = page
  }

  setPageSize(size: number) {
    this.pageSize = size
    this.currentPage = 1
  }

  // Utility actions
  clearError() {
    this.error = null
  }

  clearSelectedWorkflow() {
    this.selectedWorkflow = null
    this.nodes = []
    this.edges = []
    this.isDirty = false
  }
}

export const workflowStore = new WorkflowStore()
